// src/invoiceentry.js
import { useMemo, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';
import InvoiceTaxPanel from './InvoiceTaxPanel';
import AddressAutocomplete from './AddressAutocomplete';

/* ===============================
   HELPERS
================================== */

const safeUUID = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (e) {}
  return `id_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const money = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const roundCurrency = (n) => Number(Number(n || 0).toFixed(2));
const compareVisibleNames = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, {
  sensitivity: 'base',
  numeric: true
});

const getRetail = (item) => {
  const candidates = [
    item?.retail,
    item?.retail_price,
    item?.retailPrice,
    item?.sale_price,
    item?.salePrice,
    item?.sell_price,
    item?.sellPrice,
    item?.price,
    item?.rate
  ];
  for (const v of candidates) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

const getInvDesc = (item) => {
  const candidates = [item?.description, item?.desc, item?.details, item?.notes, item?.note];
  for (const v of candidates) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
};

const getInvQty = (item) => {
  const v =
    item?.qty_on_hand ??
    item?.qtyOnHand ??
    item?.on_hand ??
    item?.onHand ??
    item?.quantity ??
    item?.qty ??
    null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const hasInvQtyField = (item) => {
  if (!item) return false;
  return (
    ('qty_on_hand' in item) ||
    ('qtyOnHand' in item) ||
    ('on_hand' in item) ||
    ('onHand' in item) ||
    ('quantity' in item) ||
    ('qty' in item)
  );
};

/**
 * ✅ TAX LOGIC (MINIMAL + UNIVERSAL)
 * - Each line item can be taxable or not: item.taxable === true/false
 * - Notes are never taxable
 * - Discount reduces taxable base proportionally (simple + fair)
 * ✅ FIXED: Now returns stateTaxAmount and countyTaxAmount separately
 */
const computeTotals = (inv) => {
  const items = Array.isArray(inv.items) ? inv.items : [];
  const billable = items.filter(i => i.type !== 'note');

  const subtotal = billable.reduce((acc, i) => {
    const qty = Number(i.qty || 1);
    const rate = Number(i.rate || 0);
    return acc + qty * rate;
  }, 0);

  const discountStr = String(inv.discount || "0");
  const [discType, discVal] = discountStr.includes(":") ? discountStr.split(":") : ["fixed", discountStr];
  const discountValue = Number(discVal || 0);
  const rawDiscount = discType === "percentage" ? (subtotal * (discountValue / 100)) : discountValue;
  const discount = Math.min(Math.max(rawDiscount, 0), subtotal);
  const afterDisc = subtotal - discount;

  // Taxable subtotal (before discount)
  const taxableSubtotal = billable.reduce((acc, i) => {
    const isTaxable = i.taxable === true; // explicit
    if (!isTaxable) return acc;
    const qty = Number(i.qty || 1);
    const rate = Number(i.rate || 0);
    return acc + qty * rate;
  }, 0);

  // Apply discount proportionally to taxable base
  let taxableAfterDisc = taxableSubtotal;
  if (subtotal > 0 && discount > 0) {
    const ratio = Math.max(0, Math.min(1, taxableSubtotal / subtotal));
    taxableAfterDisc = Math.max(0, taxableSubtotal - (discount * ratio));
  }

  const stateTaxRate = Number(inv.sales_tax_rate || 0);
  const countyTaxRate = Number(inv.county_sales_tax_rate || 0);

  const stateTaxAmount = taxableAfterDisc * (stateTaxRate / 100);
  const countyTaxAmount = taxableAfterDisc * (countyTaxRate / 100);
  const totalTaxAmount = stateTaxAmount + countyTaxAmount;

  const total = afterDisc + totalTaxAmount;

  return { subtotal, total, stateTaxAmount, countyTaxAmount };
};

/* ===============================
   PHONE FORMATTER
================================== */
const formatPhone = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

const digitsOnly = (value) => String(value || '').replace(/\D/g, '');

/* ===============================
   TERMS → DUE DATE (DETERMINISTIC)
================================== */
const isoToday = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const addDaysISO = (iso, days) => {
  try {
    const [y, m, d] = String(iso || '').split('-').map(n => Number(n));
    if (!y || !m || !d) return isoToday();
    const base = new Date(y, m - 1, d);
    base.setDate(base.getDate() + Number(days || 0));
    const yyyy = base.getFullYear();
    const mm = String(base.getMonth() + 1).padStart(2, '0');
    const dd = String(base.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  } catch (e) {
    return isoToday();
  }
};

const normalizeTerms = (t) => String(t || '').toUpperCase().replace(/\s+/g, '');
const computeDueDate = (terms, baseISODate) => {
  const t = normalizeTerms(terms);
  const base = baseISODate || isoToday();
  if (t === 'NET15' || t === 'NET-15' || t === 'NET_15') return addDaysISO(base, 15);
  if (t === 'NET30' || t === 'NET-30' || t === 'NET_30') return addDaysISO(base, 30);
  // COD (default)
  return base;
};

/* ===============================
   CUSTOMER FIELD NORMALIZATION
   (Supports BOTH older + newer shapes)
================================== */
const getCustomerCompany = (c) =>
  String(
    c?.company ??
    c?.name ??
    ''
  ).trim();

const getCustomerContact = (c) =>
  String(
    c?.contact_name ??
    c?.contactPerson ??
    c?.contact ??
    ''
  ).trim();

const getCustomerEmail = (c) =>
  String(
    c?.email ??
    c?.billingEmail ??
    ''
  ).trim();

const getCustomerOfficePhone = (c) =>
  String(
    c?.office_phone ??
    c?.office ??
    ''
  ).trim();

const getCustomerMobilePhone = (c) =>
  String(
    c?.mobile_phone ??
    c?.mobile ??
    c?.phone ??
    ''
  ).trim();

const getCustomerTerms = (c) =>
  String(
    c?.terms ??
    'COD'
  ).trim() || 'COD';

const getCustomerAddressLine = (c) => {
  // Prefer split fields if present, else fallback to single address string
  const street = String(c?.street || '').trim();
  const city = String(c?.city || '').trim();
  const state = String(c?.state || '').trim();
  const zip = String(c?.zip || '').trim();
  const addrLine = [street, [city, state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const fallback = String(c?.address || '').trim();
  return addrLine || fallback;
};

/* ===============================
   QUICK ADD CUSTOMER MODAL
   (UI unchanged, storage changed to DB in save handler)
================================== */
const QuickAddCustomerModal = ({ onClose, onSave }) => {
  const [form, setForm] = useState({
    company: '',
    contact: '',
    email: '',
    office: '',
    mobile: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    notes: '',
    terms: 'COD'
  });

  const styles = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 4000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '14px' },
    modal: { background: '#fff', border: '10px solid black', width: '100%', maxWidth: '720px', maxHeight: '90vh', overflowY: 'auto' },
    header: { padding: '14px', borderBottom: '6px solid black', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 900 },
    body: { padding: '14px' },
    input: { padding: '12px', border: '4px solid black', fontWeight: 900, width: '100%', fontSize: '16px', boxSizing: 'border-box' },
    row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
    rowAddr: { display: 'grid', gridTemplateColumns: '2fr 1.2fr 0.6fr 1fr', gap: '10px' },
    btnRow: { display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '14px' },
    saveBtn: { backgroundColor: 'black', color: '#FFA500', border: '5px solid #E8761D', padding: '14px 16px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase' },
    cancelBtn: { backgroundColor: 'black', color: '#FFA500', border: '5px solid #E8761D', padding: '14px 16px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase' }
  };

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = () => {
    if (!form.company.trim() && !form.contact.trim()) {
      alert('Please enter at least a COMPANY or CONTACT.');
      return;
    }
    onSave({ ...form });
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div>QUICK ADD CUSTOMER</div>
          <button className="jf-hover-blue" style={styles.cancelBtn} onClick={onClose}>CLOSE</button>
        </div>

        <div style={styles.body}>
          <div style={{ display: 'grid', gap: '10px' }}>
            <div style={styles.row2}>
              <input style={styles.input} placeholder="CUSTOMER NAME / COMPANY" value={form.company} onChange={(e) => setField('company', e.target.value)} />
              <input style={styles.input} placeholder="CONTACT (OPTIONAL)" value={form.contact} onChange={(e) => setField('contact', e.target.value)} />
            </div>

            <input style={styles.input} placeholder="EMAIL" value={form.email} onChange={(e) => setField('email', e.target.value)} />

            <div style={styles.row2}>
              <input
                style={styles.input}
                placeholder="OFFICE PHONE"
                value={form.office}
                onChange={(e) => setField('office', formatPhone(e.target.value))}
              />
              <input
                style={styles.input}
                placeholder="MOBILE PHONE"
                value={form.mobile}
                onChange={(e) => setField('mobile', formatPhone(e.target.value))}
              />
            </div>

            <div style={styles.rowAddr}>
              <AddressAutocomplete
                inputStyle={styles.input}
                placeholder="STREET"
                value={form.street}
                onChange={(v) => setField('street', v)}
                onAddressSelect={({ street, city, state, zip }) => {
                  setField('street', street);
                  setField('city', city);
                  setField('state', state.slice(0, 2));
                  setField('zip', zip);
                }}
              />
              <input style={styles.input} placeholder="CITY" value={form.city} onChange={(e) => setField('city', e.target.value)} />
              <input style={styles.input} placeholder="ST" maxLength={2} value={form.state} onChange={(e) => setField('state', e.target.value.toUpperCase())} />
              <input style={styles.input} placeholder="ZIP" maxLength={5} value={form.zip} onChange={(e) => setField('zip', e.target.value)} />
            </div>

            <select style={styles.input} value={form.terms} onChange={(e) => setField('terms', e.target.value)}>
              <option value="COD">TERMS: COD (DUE TODAY)</option>
              <option value="NET15">TERMS: NET 15</option>
              <option value="NET30">TERMS: NET 30</option>
            </select>

            <textarea style={{ ...styles.input, height: '90px' }} placeholder="NOTES" value={form.notes} onChange={(e) => setField('notes', e.target.value)} />

            <div style={styles.btnRow}>
              <button className="jf-hover-blue" style={styles.saveBtn} onClick={submit}>SAVE CUSTOMER</button>
              <button className="jf-hover-blue" style={styles.cancelBtn} onClick={onClose}>CANCEL</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ===============================
   COMPONENT
================================== */
const InvoiceEntry = ({
  invoice,
  setInvoice,
  customers,
  inventory,
  cannedJobs,
  onSave
}) => {
  const [custSearch, setCustSearch] = useState(invoice.company || invoice.customer_name || '');
  const [showCustResults, setShowCustResults] = useState(false);

  const [showPartsPicker, setShowPartsPicker] = useState(false);
  const [showJobsPicker, setShowJobsPicker] = useState(false);
  const [partSearch, setPartSearch] = useState('');
  const [jobSearch, setJobSearch] = useState('');
  const [savedNotes, setSavedNotes] = useState([]);
  const [savedNoteSearches, setSavedNoteSearches] = useState({});
  const [openSavedNoteRow, setOpenSavedNoteRow] = useState(null);
  const noteTextareaRefs = useRef(new Map());
  const pendingNoteFocusRef = useRef(null);

  const [showQuickAddCustomer, setShowQuickAddCustomer] = useState(false);

  // local in-memory list to immediately include newly added DB customers (NO localStorage)
  const [localAddedCustomers, setLocalAddedCustomers] = useState([]);

  // ✅ NEW: Fetch settings from database
  const [settingsData, setSettingsData] = useState({
    business: {},
    branding: {},
    tax: { states: [] },
    discounts: []
  });

  const mergedCustomers = useMemo(() => {
    const base = Array.isArray(customers) ? customers : [];
    const added = Array.isArray(localAddedCustomers) ? localAddedCustomers : [];
    const all = [...added, ...base];

    // de-dupe by id
    const seen = new Set();
    return all.filter(c => {
      const id = String(c?.id ?? '');
      if (!id) return true;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [customers, localAddedCustomers]);

  const isLockedFromEstimate = useMemo(() => {
    const hasSource =
      !!invoice?.source_estimate_id ||
      !!invoice?.sourceEstimateId ||
      !!invoice?.source_estimate_number ||
      !!invoice?.sourceEstimateNumber ||
      !!invoice?.estimate_id ||
      !!invoice?.estimateId ||
      !!invoice?.estimate_number ||
      !!invoice?.estimateNumber;

    const explicitLock =
      invoice?.locked_from_estimate === true ||
      invoice?.lock_from_estimate === true ||
      invoice?.converted_from_estimate === true ||
      invoice?.from_estimate === true ||
      invoice?.read_only === true ||
      invoice?.readonly === true;

    const approvedSignal = String(
      invoice?.source_estimate_status ||
      invoice?.estimate_status ||
      invoice?.sourceStatus ||
      invoice?.estimateStatus ||
      ''
    ).toLowerCase() === 'approved';

    const allowEditOverride = invoice?.allow_edit_after_convert === true;

    return (hasSource || explicitLock || approvedSignal) && !allowEditOverride;
  }, [invoice]);

  // ✅ NEW: Fetch all settings on mount
  useEffect(() => {
    const fetchAllSettings = async () => {
      try {
        const API = getApiBase();

        const taxRes = await fetch(`${API}/api/settings/taxes`, {
          headers: { ...getTenantHeaders() }
        });
        const taxData = taxRes.ok ? await taxRes.json() : {};

        const discRes = await fetch(`${API}/api/settings/discounts`, {
          headers: { ...getTenantHeaders() }
        });
        const discData = discRes.ok ? await discRes.json() : [];

        const states = Array.isArray(taxData?.data?.states) ? taxData.data.states : [];
        const homeState = states.find(s => s.is_home_state) || states[0] || null;

        setSettingsData({
          business: {},
          branding: {},
          tax: { states },
          discounts: Array.isArray(discData) ? discData : []
        });

        if (
          !isLockedFromEstimate &&
          Array.isArray(invoice.selected_local_tax_ids) &&
          (!invoice.state_abbr || invoice.state_abbr === '') &&
          homeState
        ) {
          const defCounty = (homeState.counties || []).find(c => c.is_default);
          applyInvoicePatch({
            sales_tax_rate: Number(homeState.state_sales_tax_rate || 0),
            state_abbr: homeState.state_abbr || '',
            state_name: homeState.state_name || '',
            county_sales_tax_rate: defCounty ? Number(defCounty.rate || 0) : 0,
            county_name: defCounty ? defCounty.name : '',
            selected_local_tax_ids: defCounty ? [Number(defCounty.id)] : [],
          });
        }
      } catch (e) {
        console.error('Failed to load settings:', e);
      }
    };

    fetchAllSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchSavedNotes = async () => {
      try {
        const response = await fetch(`${getApiBase()}/api/settings/saved-notes`, {
          headers: { ...getTenantHeaders() }
        });
        if (!response.ok) return;
        const data = await response.json().catch(() => []);
        setSavedNotes(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Failed to load saved notes:', e);
      }
    };

    fetchSavedNotes();
  }, []);

  const styles = {
    card: { backgroundColor: '#fff', border: '8px solid black', padding: '18px' },
    label: { fontWeight: 900, display: 'block', marginBottom: '6px', fontSize: '12px' },
    neonLabel: { fontWeight: 900, display: 'block', marginBottom: '6px', fontSize: '12px', color: '#FFA500' },

    input: { padding: '12px', border: '4px solid black', fontWeight: 900, width: '100%', fontSize: '16px', boxSizing: 'border-box' },

    descArea: {
      padding: '12px',
      border: '4px solid black',
      fontWeight: 900,
      width: '100%',
      fontSize: '16px',
      boxSizing: 'border-box',
      resize: 'none',
      overflow: 'hidden',
      lineHeight: '1.2',
      minHeight: '48px'
    },

    savedNoteBox: { position: 'relative', marginBottom: '8px' },
    savedNoteSearch: { padding: '10px', border: '3px solid black', fontWeight: 900, width: '100%', fontSize: '14px', boxSizing: 'border-box' },
    savedNoteDropdown: { border: '3px solid black', position: 'absolute', backgroundColor: 'white', zIndex: 110, width: '100%', maxHeight: '180px', overflowY: 'auto', boxSizing: 'border-box' },
    savedNoteOption: { padding: '10px', cursor: 'pointer', borderBottom: '2px solid black', fontWeight: 900 },

    dropdown: { border: '4px solid black', position: 'absolute', backgroundColor: 'white', zIndex: 100, width: '100%', maxHeight: '220px', overflowY: 'auto' },

    customerRow: { display: 'flex', gap: '10px', alignItems: 'stretch', flexWrap: 'wrap' },
    customerBox: { flex: 1, minWidth: '260px', position: 'relative' },
    customerBtn: { backgroundColor: 'black', color: '#FFA500', border: '4px solid #E8761D', fontWeight: 900, cursor: 'pointer', padding: '12px 14px', textTransform: 'uppercase', minWidth: '220px' },

    actionRow: { display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' },
    actionBtnLabor: { backgroundColor: '#d9ecff', color: 'black', border: '5px solid black', fontWeight: 900, cursor: 'pointer', padding: '12px 14px', fontSize: '16px' },
    actionBtnParts: { backgroundColor: '#d9fdd3', color: 'black', border: '5px solid black', fontWeight: 900, cursor: 'pointer', padding: '12px 14px', fontSize: '16px' },
    actionBtnNote: { backgroundColor: '#fff1b8', color: 'black', border: '5px solid black', fontWeight: 900, cursor: 'pointer', padding: '12px 14px', fontSize: '16px' },

    pickWrap: { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' },
    pickBox: { flex: 1, minWidth: '260px' },
    pickBtnParts: { width: '100%', padding: '14px', fontWeight: 900, border: '5px solid black', cursor: 'pointer', backgroundColor: '#d9fdd3', textTransform: 'uppercase' },
    pickBtnJobs: { width: '100%', padding: '14px', fontWeight: 900, border: '5px solid black', cursor: 'pointer', backgroundColor: '#d9ecff', textTransform: 'uppercase' },

    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 3000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '14px' },
    picker: { background: '#fff', border: '10px solid black', width: '100%', maxWidth: '620px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' },
    pickerHeader: { padding: '14px', borderBottom: '6px solid black', fontWeight: 900, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    pickerList: { overflowY: 'auto', padding: '10px' },
    pickerItem: { border: '4px solid black', padding: '12px', marginBottom: '10px', fontWeight: 900, cursor: 'pointer' },
    pickerClose: { background: 'black', color: '#FFA500', border: '4px solid #E8761D', padding: '10px 14px', fontWeight: 900, cursor: 'pointer' },

    removeBtn: { background: '#ff3b3b', color: 'black', border: '4px solid black', fontWeight: 900, cursor: 'pointer', padding: '0 14px', fontSize: '18px', height: '44px' },
    lineTotalText: { fontWeight: 900, marginTop: '6px', fontSize: '14px' },

    totalBox: { backgroundColor: 'black', color: '#FFA500', padding: '18px', border: '8px solid #E8761D', marginTop: '16px' },
    saveBtn: { backgroundColor: '#1B4332', color: '#FFA500', border: '5px solid #E8761D', padding: '18px', fontWeight: 900, fontSize: '20px', cursor: 'pointer' },

    lockBanner: {
      backgroundColor: 'black',
      color: '#FFA500',
      border: '6px solid #E8761D',
      padding: '14px',
      fontWeight: 900,
      marginBottom: '14px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    }
  };

  const disabledStyle = (base) => {
    if (!isLockedFromEstimate) return base;
    return { ...base, opacity: 0.55, cursor: 'not-allowed' };
  };

  const autoGrow = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  useLayoutEffect(() => {
    (invoice.items || []).forEach((item, idx) => {
      if (item?.type !== 'note') return;
      const key = item.id || String(idx);
      const el = noteTextareaRefs.current.get(key);
      if (!el) return;

      el.style.height = 'auto';
      const fullHeight = el.scrollHeight;
      el.style.height = `${Math.min(fullHeight, 320)}px`;
      el.style.overflowY = fullHeight > 320 ? 'auto' : 'hidden';
    });

    const pendingFocus = pendingNoteFocusRef.current;
    if (!pendingFocus) return;

    const el = noteTextareaRefs.current.get(pendingFocus.key);
    pendingNoteFocusRef.current = null;
    if (!el) return;

    el.focus({ preventScroll: true });
    window.scrollTo(pendingFocus.scrollX, pendingFocus.scrollY);
  }, [invoice.items]);

  // ✅ Customer search supports DB-first shape (company/contact_name/etc.) AND older shape (name/contactPerson/etc.)
  const customerResults = useMemo(() => {
    const q = (custSearch || '').trim().toLowerCase();
    if (!q) return [];
    return (mergedCustomers || []).filter(c => {
      const company = getCustomerCompany(c).toLowerCase();
      const contact = getCustomerContact(c).toLowerCase();
      const email = getCustomerEmail(c).toLowerCase();
      const office = getCustomerOfficePhone(c);
      const mobile = getCustomerMobilePhone(c);
      const phones = `${office} ${mobile} ${digitsOnly(office)} ${digitsOnly(mobile)}`.toLowerCase();
      return `${company} ${contact} ${email} ${phones}`.includes(q);
    });
  }, [custSearch, mergedCustomers]);

  const applyInvoicePatch = (patch) => {
    const next = { ...invoice, ...patch };

    // ✅ AUTO-FIX: ensure each non-note line has an explicit taxable boolean
    const items = Array.isArray(next.items) ? next.items : [];
    const normalizedItems = items.map((it) => {
      if (!it || it.type === 'note') return it;
      if (typeof it.taxable === 'boolean') return it;
      return { ...it, taxable: false };
    });

    if (normalizedItems !== items) {
      next.items = normalizedItems;
    }

    const { subtotal, total, stateTaxAmount, countyTaxAmount } = computeTotals(next);

    const paid = Number(next.amount_paid || 0);
    const balance_due = Math.max(0, total - paid);

    setInvoice({ ...next, subtotal, total, stateTaxAmount, countyTaxAmount, balance_due });
  };

  const handleLocalTaxSelectionChange = (selectedLocalTaxes) => {
    const selectedRecords = Array.isArray(selectedLocalTaxes) ? selectedLocalTaxes : [];
    applyInvoicePatch({
      selected_local_tax_ids: selectedRecords.map(localTax => Number(localTax.id)),
      county_sales_tax_rate: selectedRecords.reduce(
        (sum, localTax) => sum + Number(localTax.rate || 0),
        0
      ),
      county_name: selectedRecords.map(localTax => localTax.name).filter(Boolean).join(', ')
    });
  };

  const handleStateChange = (
    stateAbbr,
    stateName,
    stateSalesTaxRate,
    defaultCountyRate,
    defaultCountyName
  ) => {
    const selectedState = (settingsData.tax.states || []).find(
      state => state.state_abbr === stateAbbr
    );
    const defaultCounty = (selectedState?.counties || []).find(county => county.is_default);

    applyInvoicePatch({
      state_abbr: stateAbbr,
      state_name: stateName,
      sales_tax_rate: stateSalesTaxRate,
      county_sales_tax_rate: defaultCountyRate,
      county_name: defaultCountyName,
      selected_local_tax_ids: defaultCounty ? [Number(defaultCounty.id)] : []
    });
  };

  const addLaborLine = () => {
    if (isLockedFromEstimate) return;
    const items = [
      ...(invoice.items || []),
      { id: safeUUID(), type: 'labor', description: '', qty: 1, rate: '', taxable: false }
    ];
    applyInvoicePatch({ items });
  };

  const addPartLine = () => {
    if (isLockedFromEstimate) return;
    const items = [
      ...(invoice.items || []),
      { id: safeUUID(), type: 'parts_custom', description: '', qty: 1, cost: '', markup: '', rate: '', taxable: false }
    ];
    applyInvoicePatch({ items });
  };

  const addNoteLine = () => {
    if (isLockedFromEstimate) return;
    const items = [...(invoice.items || []), { id: safeUUID(), type: 'note', description: '' }];
    applyInvoicePatch({ items });
  };

  const removeItem = (idx) => {
    if (isLockedFromEstimate) return;
    const items = (invoice.items || []).filter((_, i) => i !== idx);
    applyInvoicePatch({ items });
  };

  const updateItem = (idx, patch) => {
    if (isLockedFromEstimate) return;
    const items = [...(invoice.items || [])];
    items[idx] = { ...items[idx], ...patch };

    if (items[idx].type === 'parts_custom') {
      if (items[idx].cost === '' || items[idx].cost == null) {
        items[idx].rate = '';
      } else {
        const cost = Number(items[idx].cost || 0);
        const markup = Number(items[idx].markup || 0);
        const retail = cost * (1 + markup / 100);
        items[idx].rate = Number.isFinite(retail) ? roundCurrency(retail) : Number(items[idx].rate || 0);
      }
    }

    applyInvoicePatch({ items });
  };

  const addItemFromInventory = (source) => {
    if (isLockedFromEstimate) return;

    const name = source?.name || 'Inventory Item';
    const extra = getInvDesc(source);
    const description = extra ? `${name} - ${extra}` : name;

    const item = {
      id: safeUUID(),
      type: 'parts_inventory',
      description,
      qty: 1,
      rate: getRetail(source),
      source_id: source?.id || null,
      taxable: false
    };

    const items = [...(invoice.items || []), item];
    applyInvoicePatch({ items });
  };

  const addItemFromJob = (job) => {
    if (isLockedFromEstimate) return;

    const item = {
      id: safeUUID(),
      type: 'labor',
      description: job?.jobName || 'Job',
      qty: 1,
      rate: Number(job?.price || 0),
      source_id: job?.id || null,
      taxable: false
    };
    const items = [...(invoice.items || []), item];
    applyInvoicePatch({ items });
  };

  const patchTermsAndDueDate = (termsValue) => {
    const terms = normalizeTerms(termsValue || invoice?.terms || 'COD') || 'COD';
    const base = invoice?.invoice_date || invoice?.date || isoToday(); // support either key if already used elsewhere
    const due_date = computeDueDate(terms, base);

    // Store BOTH on the invoice record (DB truth once saved)
    applyInvoicePatch({ terms, due_date });
  };

  // ✅ Quick add customer saves to DB and updates invoice + in-memory list
  // ✅ FIX: only send VALID customers table columns (backend builds INSERT from payload keys)
  const saveQuickCustomer = async (newCustomerForm) => {
    if (isLockedFromEstimate) return;

    try {
      const API = getApiBase();

      // Build address line once (also stored in legacy "address" field)
      const street = String(newCustomerForm.street || '').trim();
      const city = String(newCustomerForm.city || '').trim();
      const state = String(newCustomerForm.state || '').trim();
      const zip = String(newCustomerForm.zip || '').trim();

      const addrLine = [
        street,
        [city, state, zip].filter(Boolean).join(' ')
      ].filter(Boolean).join(', ');

      // IMPORTANT: customers.name is NOT NULL in DB.
      const name = String(newCustomerForm.company || newCustomerForm.contact || '').trim();
      const rawContact = String(newCustomerForm.contact || "").trim();
      const rawCompany = String(newCustomerForm.company || "").trim();

      // If CONTACT matches CUSTOMER NAME/COMPANY exactly, treat it as accidental autofill
      const contactPerson = (rawContact && rawContact !== rawCompany) ? rawContact : "";
      const email = String(newCustomerForm.email || '').trim();

      if (!name && !contactPerson) {
        alert('Please enter at least a COMPANY or CONTACT.');
        return;
      }

      // ✅ Only DB-valid columns (DESCRIBE customers)
      const payload = {
        name: name || contactPerson, // guarantee NOT NULL
        contactPerson,
        billingEmail: email,
        email,
        phone: digitsOnly(newCustomerForm.mobile || newCustomerForm.office || ''),
        office_phone: digitsOnly(newCustomerForm.office || ''),
        mobile_phone: digitsOnly(newCustomerForm.mobile || ''),
        street,
        city,
        state,
        zip,
        address: addrLine,
        notes: String(newCustomerForm.notes || '').trim(),
        terms: String(newCustomerForm.terms || 'COD').trim()
      };

      const res = await fetch(`${API}/api/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
        body: JSON.stringify(payload)
      });

      let created = null;
      try {
        created = await res.json();
      } catch (e) {
        created = null;
      }

      if (!res.ok) {
        const msg = created?.error || 'Customer create failed';
        throw new Error(msg);
      }

      const createdId =
        (created && (created.id ?? created.insertId ?? created.customer_id ?? created.customerId)) ??
        Date.now();

      // Normalize created customer to DB-first shape for search + selection
      // Keep "company/contact_name" aliases so existing helpers work.
      const createdCustomer = {
        ...(created && typeof created === 'object' ? created : {}),
        id: createdId,

        // canonical / legacy fields
        name: String(created?.name || payload.name || '').trim(),
        contactPerson: String(created?.contactPerson || payload.contactPerson || '').trim(),
        billingEmail: String(created?.billingEmail || payload.billingEmail || '').trim(),
        email: String(created?.email || payload.email || '').trim(),
        phone: String(created?.phone || payload.phone || '').trim(),
        terms: String(created?.terms || payload.terms || 'COD').trim(),
        notes: String(created?.notes || payload.notes || '').trim(),

        office_phone: String(created?.office_phone || payload.office_phone || '').trim(),
        mobile_phone: String(created?.mobile_phone || payload.mobile_phone || '').trim(),
        street: String(created?.street || payload.street || '').trim(),
        city: String(created?.city || payload.city || '').trim(),
        state: String(created?.state || payload.state || '').trim(),
        zip: String(created?.zip || payload.zip || '').trim(),
        address: String(created?.address || payload.address || '').trim(),

        // aliases used by your normalization helpers
        company: String(created?.name || payload.name || '').trim(),
        contact_name: String(created?.contactPerson || payload.contactPerson || '').trim()
      };

      setLocalAddedCustomers(prev => [{ ...createdCustomer }, ...prev]);

      // Apply to invoice + set terms/due date deterministically
      const company = getCustomerCompany(createdCustomer);
      const contact = getCustomerContact(createdCustomer);
      const custEmail = getCustomerEmail(createdCustomer);
      const phone = formatPhone(getCustomerMobilePhone(createdCustomer) || getCustomerOfficePhone(createdCustomer));
      const address = getCustomerAddressLine(createdCustomer);
      const terms = getCustomerTerms(createdCustomer);
      const base = invoice?.invoice_date || invoice?.date || isoToday();
      const due_date = computeDueDate(terms, base);

      applyInvoicePatch({
        customer_id: createdCustomer.id,
        company,
        customer_name: contact,
        customer_email: custEmail,
        customer_phone: phone,
        customer_address: address,
        terms,
        due_date
      });

      setCustSearch(company || contact || '');
      setShowCustResults(false);
      setShowQuickAddCustomer(false);
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'CUSTOMER ADDED!' },
      }));
    } catch (e) {
      console.error(e);
      alert(e.message || 'FAILED TO SAVE CUSTOMER');
    }
  };

  useEffect(() => {
    setCustSearch(invoice.company || invoice.customer_name || '');
  }, [invoice?.company, invoice?.customer_name]);

  return (
    <div style={styles.card}>
      {/* HOVER (NEON BLUE) — buttons only */}
      <style>{`
        .jf-hover-blue { transition: filter 120ms ease, transform 120ms ease; }
        .jf-hover-blue:hover {
          background: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
          filter: brightness(1.02);
        }
        .jf-hover-blue:active { transform: translateY(1px); }
        .jf-hover-blue:disabled,
        .jf-hover-blue[disabled] { opacity: 0.55; cursor: not-allowed; transform: none; filter: none; }
      `}</style>

      {isLockedFromEstimate && (
        <div style={styles.lockBanner}>
          🔒 LOCKED — CONVERTED FROM APPROVED ESTIMATE. DOLLAR AMOUNTS & LINE ITEMS CANNOT BE CHANGED.
        </div>
      )}

      {/* CUSTOMER + QUICK ADD */}
      <div style={{ marginBottom: '14px' }}>
        <label style={styles.label}>SELECT CUSTOMER</label>

        <div style={styles.customerRow}>
          <div style={styles.customerBox}>
            <input
              style={disabledStyle(styles.input)}
              value={custSearch}
              disabled={isLockedFromEstimate}
              onChange={(e) => {
                if (isLockedFromEstimate) return;
                setCustSearch(e.target.value);
                setShowCustResults(true);
              }}
              onFocus={() => {
                if (isLockedFromEstimate) return;
                setShowCustResults(true);
              }}
              onBlur={() => setTimeout(() => setShowCustResults(false), 150)}
              placeholder={isLockedFromEstimate ? 'LOCKED (FROM ESTIMATE)' : 'Start typing customer name...'}
            />

            {!isLockedFromEstimate && showCustResults && custSearch && customerResults.length > 0 && (
              <div style={styles.dropdown}>
                {customerResults.map(c => {
                  const company = getCustomerCompany(c);
                  const contact = getCustomerContact(c);
                  return (
                    <div
                      key={c.id}
                      style={{ padding: '10px', cursor: 'pointer', borderBottom: '2px solid black', fontWeight: 900 }}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        const email = getCustomerEmail(c);
                        const phone = formatPhone(getCustomerMobilePhone(c) || getCustomerOfficePhone(c));
                        const address = getCustomerAddressLine(c);
                        const terms = getCustomerTerms(c);
                        const base = invoice?.invoice_date || invoice?.date || isoToday();
                        const due_date = computeDueDate(terms, base);

                        applyInvoicePatch({
                          customer_id: c.id,
                          company,
                          customer_name: contact,
                          customer_email: email,
                          customer_phone: phone,
                          customer_address: address,
                          terms,
                          due_date
                        });
                        setCustSearch(company || contact || '');
                        setShowCustResults(false);
                      }}
                    >
                      {(company || '(No Company)')} | {(contact || '(No Contact)')}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <button
            className="jf-hover-blue"
            style={disabledStyle(styles.customerBtn)}
            onClick={() => {
              if (isLockedFromEstimate) return;
              setShowQuickAddCustomer(true);
            }}
            disabled={isLockedFromEstimate}
            title={isLockedFromEstimate ? 'Locked from estimate conversion' : ''}
          >
            + ADD CUSTOMER
          </button>
        </div>
      </div>

      {/* ADD BUTTONS */}
      <div style={styles.actionRow}>
        <button className="jf-hover-blue" style={disabledStyle(styles.actionBtnLabor)} onClick={addLaborLine} disabled={isLockedFromEstimate}>
          + ADD LABOR LINE
        </button>
        <button className="jf-hover-blue" style={disabledStyle(styles.actionBtnParts)} onClick={addPartLine} disabled={isLockedFromEstimate}>
          + ADD PART LINE
        </button>
        <button className="jf-hover-blue" style={disabledStyle(styles.actionBtnNote)} onClick={addNoteLine} disabled={isLockedFromEstimate}>
          + ADD NOTE LINE
        </button>
      </div>

      {/* PICKERS */}
      <div style={styles.pickWrap}>
        <div style={styles.pickBox}>
          <label style={styles.label}>PART / ITEM (INVENTORY)</label>
          <button
            className="jf-hover-blue"
            style={disabledStyle(styles.pickBtnParts)}
            onClick={() => { if (!isLockedFromEstimate) setShowPartsPicker(true); }}
            disabled={isLockedFromEstimate}
          >
            PICK A PART
          </button>
        </div>
        <div style={styles.pickBox}>
          <label style={styles.label}>CANNED JOB (LABOR)</label>
          <button
            className="jf-hover-blue"
            style={disabledStyle(styles.pickBtnJobs)}
            onClick={() => { if (!isLockedFromEstimate) setShowJobsPicker(true); }}
            disabled={isLockedFromEstimate}
          >
            PICK A JOB
          </button>
        </div>
      </div>

      {/* LINE ITEMS */}
      <style>{`
        .jf-item { margin-bottom: 14px; }
        .jf-descRow { margin-bottom: 8px; }
        .jf-numRow { display:flex; gap:10px; align-items:center; flex-wrap: nowrap; }
        .jf-w-qty { width: 90px; }
        .jf-w-rate { width: 140px; }
        .jf-w-tax { width: 86px; }
        .jf-x { width: 54px; }
        .jf-customPartRow {
          display: grid;
          grid-template-columns: 100px minmax(110px, 1fr) minmax(110px, 1fr) minmax(110px, 1fr) 100px minmax(110px, 1fr) 64px;
          gap: 10px;
          align-items: end;
        }
        .jf-customPartField { min-width: 0; }
        .jf-customPartLabel { display: block; margin-bottom: 6px; font-size: 12px; font-weight: 900; }
        .jf-lineNumber::-webkit-outer-spin-button,
        .jf-lineNumber::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .jf-lineNumber[type="number"] { -moz-appearance: textfield; appearance: textfield; }
        .jf-standardItemRow {
          display: grid;
          grid-template-columns: 100px minmax(110px, 160px) 100px minmax(110px, 140px) 64px;
          gap: 10px;
          align-items: end;
        }

        @media (max-width: 520px) {
          .jf-numRow { flex-wrap: wrap; }
          .jf-w-qty { width: 100px; }
          .jf-w-rate { width: 140px; }
          .jf-customPartRow { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .jf-standardItemRow { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
      `}</style>

      {(invoice.items || []).map((item, idx) => {
        const isNote = item.type === 'note';
        const isCustomPart = item.type === 'parts_custom';
        const qty = Number(item.qty || 1);
        const rate = Number(item.rate || 0);
        const lineTotal = isNote ? 0 : qty * rate;
        const key = item.id || String(idx);

        if (isNote) {
          const savedNoteSearch = savedNoteSearches[key] || '';
          const matchingSavedNotes = savedNotes.filter(savedNote =>
            !savedNoteSearch || String(savedNote.name || '').toLowerCase().includes(savedNoteSearch.toLowerCase())
          );

          return (
            <div key={key} className="jf-item">
              {!isLockedFromEstimate && savedNotes.length > 0 && (
                <div style={styles.savedNoteBox}>
                  <input
                    type="text"
                    value={savedNoteSearch}
                    placeholder="SEARCH SAVED NOTES BY TITLE (OPTIONAL)..."
                    style={styles.savedNoteSearch}
                    onFocus={() => setOpenSavedNoteRow(key)}
                    onBlur={() => setTimeout(() => setOpenSavedNoteRow(null), 150)}
                    onChange={(e) => {
                      setSavedNoteSearches(prev => ({ ...prev, [key]: e.target.value }));
                      setOpenSavedNoteRow(key);
                    }}
                  />

                  {openSavedNoteRow === key && matchingSavedNotes.length > 0 && (
                    <div style={styles.savedNoteDropdown}>
                      {matchingSavedNotes.map(savedNote => (
                        <div
                          key={savedNote.id}
                          style={styles.savedNoteOption}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            pendingNoteFocusRef.current = {
                              key,
                              scrollX: window.scrollX,
                              scrollY: window.scrollY
                            };
                            updateItem(idx, { description: savedNote.note_text });
                            setSavedNoteSearches(prev => ({ ...prev, [key]: savedNote.name || '' }));
                            setOpenSavedNoteRow(null);
                          }}
                        >
                          {savedNote.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="jf-descRow">
                <textarea
                  ref={(el) => {
                    if (el) noteTextareaRefs.current.set(key, el);
                    else noteTextareaRefs.current.delete(key);
                  }}
                  style={disabledStyle(styles.descArea)}
                  value={item.description}
                  rows={1}
                  placeholder="NOTE (prints, does not affect totals)…"
                  disabled={isLockedFromEstimate}
                  onChange={(e) => updateItem(idx, { description: e.target.value })}
                />
              </div>
              <div className="jf-numRow">
                <button className="jf-hover-blue" style={disabledStyle(styles.removeBtn)} onClick={() => removeItem(idx)} disabled={isLockedFromEstimate}>
                  X
                </button>
              </div>
            </div>
          );
        }

        const taxableChecked = item.taxable === true;

        return (
          <div key={key} className="jf-item">
            <div className="jf-descRow">
              <textarea
                style={disabledStyle(styles.descArea)}
                value={item.description}
                rows={1}
                placeholder={item.type === 'labor' ? 'Labor description…' : 'Part description…'}
                disabled={isLockedFromEstimate}
                onInput={(e) => autoGrow(e.target)}
                onChange={(e) => updateItem(idx, { description: e.target.value })}
              />
            </div>

            {isCustomPart && (
              <div className="jf-customPartRow">
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">QTY</label>
                  <input className="jf-lineNumber" style={disabledStyle(styles.input)} type="number" value={item.qty} disabled={isLockedFromEstimate} onChange={(e) => updateItem(idx, { qty: e.target.value })} onWheel={e => e.target.blur()} />
                </div>
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">COST</label>
                  <input className="jf-lineNumber" style={disabledStyle(styles.input)} type="number" value={item.cost ?? ''} disabled={isLockedFromEstimate} onChange={(e) => updateItem(idx, { cost: e.target.value })} placeholder="COST" onWheel={e => e.target.blur()} />
                </div>
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">MARKUP %</label>
                  <input className="jf-lineNumber" style={disabledStyle(styles.input)} type="number" value={item.markup ?? ''} disabled={isLockedFromEstimate} onChange={(e) => updateItem(idx, { markup: e.target.value })} placeholder="MARKUP %" onWheel={e => e.target.blur()} />
                </div>
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">RETAIL</label>
                  <input className="jf-lineNumber" style={disabledStyle(styles.input)} type="number" value={item.rate} disabled={isLockedFromEstimate} onChange={(e) => updateItem(idx, { rate: e.target.value })} placeholder="RETAIL" onWheel={e => e.target.blur()} />
                </div>
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">TAX</label>
                  <label style={{ ...disabledStyle(styles.input), display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: isLockedFromEstimate ? 'not-allowed' : 'pointer' }}>
                    <input type="checkbox" checked={taxableChecked} disabled={isLockedFromEstimate} onChange={(e) => updateItem(idx, { taxable: e.target.checked })} style={{ transform: 'scale(1.2)' }} />
                    TAX
                  </label>
                </div>
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">TOTAL</label>
                  <div style={{ ...styles.input, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>{money(lineTotal)}</div>
                </div>
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">ACTIONS</label>
                  <button className="jf-hover-blue jf-x" style={disabledStyle({ ...styles.removeBtn, width: '100%' })} onClick={() => removeItem(idx)} disabled={isLockedFromEstimate}>X</button>
                </div>
              </div>
            )}

            {!isCustomPart && (
              <div className="jf-standardItemRow">
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">QTY</label>
                  <input className="jf-lineNumber" style={disabledStyle(styles.input)} type="number" value={item.qty} disabled={isLockedFromEstimate} onChange={(e) => updateItem(idx, { qty: e.target.value })} onWheel={e => e.target.blur()} />
                </div>
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">{item.type === 'labor' ? 'RATE' : 'RETAIL'}</label>
                  <input className="jf-lineNumber" style={disabledStyle(styles.input)} type="number" value={item.rate} disabled={isLockedFromEstimate} onChange={(e) => updateItem(idx, { rate: e.target.value })} placeholder={item.type === 'labor' ? 'RATE' : 'RETAIL'} onWheel={e => e.target.blur()} />
                </div>
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">TAX</label>
                  <label style={{ ...disabledStyle(styles.input), display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: isLockedFromEstimate ? 'not-allowed' : 'pointer' }}>
                    <input type="checkbox" checked={taxableChecked} disabled={isLockedFromEstimate} onChange={(e) => updateItem(idx, { taxable: e.target.checked })} style={{ transform: 'scale(1.2)' }} />
                    TAX
                  </label>
                </div>
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">TOTAL</label>
                  <div style={{ ...styles.input, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>{money(lineTotal)}</div>
                </div>
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">ACTIONS</label>
                  <button className="jf-hover-blue jf-x" style={disabledStyle({ ...styles.removeBtn, width: '100%' })} onClick={() => removeItem(idx)} disabled={isLockedFromEstimate}>X</button>
                </div>
              </div>
            )}

            <div style={styles.lineTotalText}>LINE TOTAL: {money(lineTotal)}</div>
          </div>
        );
      })}

      {/* TOTALS */}
      <div style={styles.totalBox}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px', marginBottom: '10px' }}>
          <div style={{ ...styles.input, background: '#111', color: '#FFA500' }}>
            SUBTOTAL: {money(invoice.subtotal)}
          </div>

          <select
            style={disabledStyle(styles.input)}
            value={invoice.discount_selection || '0'}
            disabled={isLockedFromEstimate}
            onChange={e => applyInvoicePatch({ discount: e.target.value, discount_selection: e.target.value })}
          >
            <option value="0">COUPON / DISCOUNT</option>
            {[...(settingsData.discounts || [])]
              .sort((a, b) => compareVisibleNames(a.name, b.name))
              .map(c => (
              <option key={c.id} value={`${c.type || "fixed"}:${Number(c.value || 0)}:${c.id}`}>
                {c.name} ({c.type === "percentage" ? `${c.value}%` : money(c.value)} OFF)
              </option>
              ))}
          </select>

          {(() => {
            const discountStr = String(invoice.discount || '0');
            if (!discountStr.includes(':')) return null;
            const parts = discountStr.split(':');
            const discType = parts[0];
            const discId = parts[2];
            const matched = (settingsData.discounts || []).find(d => String(d.id) === String(discId));
            if (!matched) return null;
            return (
              <>
                <div style={{ ...styles.input, background: '#111', color: '#FFA500', fontSize: '13px' }}>
                  DISCOUNT: {matched.name || matched.description || ''}
                </div>
                {discType === 'variable' && (
                  <div>
                    <label style={styles.neonLabel}>DISCOUNT AMOUNT ($)</label>
                    <input
                      style={disabledStyle(styles.input)}
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      disabled={isLockedFromEstimate}
                      value={(() => {
                        const v = parts[1];
                        return v === '0' || v === '' ? '' : v;
                      })()}
                      onChange={e => {
                        const amt = e.target.value || '0';
                        applyInvoicePatch({ discount: `variable:${amt}:${discId}` });
                      }}
                      onWheel={e => e.target.blur()}
                    />
                  </div>
                )}
              </>
            );
          })()}

          <InvoiceTaxPanel
            states={settingsData.tax.states}
            selectedStateAbbr={invoice.state_abbr || ''}
            salesTaxRate={invoice.sales_tax_rate}
            selectedCountyName={invoice.county_name || ''}
            selectedLocalTaxIds={invoice.selected_local_tax_ids}
            disabled={isLockedFromEstimate}
            onSalesTaxChange={val => applyInvoicePatch({ sales_tax_rate: val })}
            onCountyChange={(rate, countyName) => applyInvoicePatch({ county_sales_tax_rate: rate, county_name: countyName })}
            onLocalTaxSelectionChange={handleLocalTaxSelectionChange}
            onStateChange={handleStateChange}
            inputStyle={disabledStyle(styles.input)}
            neonLabelStyle={styles.neonLabel}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <h2 style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '0 10px', margin: 0, maxWidth: '100%' }}>
            <span style={{ fontSize: 'clamp(18px, 6vw, 44px)' }}>TOTAL:</span>
            <span style={{ fontSize: 'clamp(16px, 6vw, 44px)', whiteSpace: 'nowrap' }}>{money(invoice.total)}</span>
          </h2>

          <button
            className="jf-hover-blue"
            style={styles.saveBtn}
            onClick={() => {
              // Ensure terms/due_date are present even if user never touched customer after load
              if (!invoice?.terms || !invoice?.due_date) {
                patchTermsAndDueDate(invoice?.terms || 'COD');
              }
              onSave(invoice);
            }}
          >
            SAVE INVOICE
          </button>

          {isLockedFromEstimate && (
            <div style={{ fontWeight: 900, color: '#FFA500' }}>
              TIP: This invoice is locked to protect approved pricing. Just hit SAVE INVOICE.
            </div>
          )}
        </div>
      </div>

      {/* PARTS PICKER */}
      {showPartsPicker && !isLockedFromEstimate && (
        <div style={styles.overlay} onClick={() => { setShowPartsPicker(false); setPartSearch(''); }}>
          <div style={styles.picker} onClick={(e) => e.stopPropagation()}>
            <div style={styles.pickerHeader}>
              <div>PICK A PART</div>
              <button className="jf-hover-blue" style={styles.pickerClose} onClick={() => { setShowPartsPicker(false); setPartSearch(''); }}>CLOSE</button>
            </div>

            <input
              type="text"
              placeholder="Search parts..."
              value={partSearch}
              onChange={(e) => setPartSearch(e.target.value)}
              style={{ margin: '10px', padding: '8px', border: '3px solid black', fontWeight: 700, fontSize: '14px', width: 'calc(100% - 20px)', boxSizing: 'border-box' }}
              autoFocus
            />

            <div style={styles.pickerList}>
              {(inventory || []).filter(i => !partSearch || (i.name || '').toLowerCase().includes(partSearch.toLowerCase())).map(i => {
                const qty = getInvQty(i);
                const out = hasInvQtyField(i) && qty !== null && qty <= 0;
                const retail = getRetail(i);

                return (
                  <div
                    key={i.id}
                    style={{ ...styles.pickerItem, opacity: out ? 0.45 : 1, background: out ? '#f2f2f2' : '#fff' }}
                    onClick={() => {
                      if (out) return;
                      addItemFromInventory(i);
                      setShowPartsPicker(false);
                      setPartSearch('');
                    }}
                  >
                    {i.name} — {money(retail)}
                    {hasInvQtyField(i) && qty !== null ? ` (QTY: ${qty})` : ''}
                    {out ? ' — OUT OF STOCK' : ''}
                  </div>
                );
              })}

              {(inventory || []).filter(i => !partSearch || (i.name || '').toLowerCase().includes(partSearch.toLowerCase())).length === 0 && (
                <div style={{ fontWeight: 900, padding: '12px' }}>{(inventory || []).length === 0 ? 'No inventory items yet.' : 'No parts match your search.'}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* JOBS PICKER */}
      {showJobsPicker && !isLockedFromEstimate && (
        <div style={styles.overlay} onClick={() => { setShowJobsPicker(false); setJobSearch(''); }}>
          <div style={styles.picker} onClick={(e) => e.stopPropagation()}>
            <div style={styles.pickerHeader}>
              <div>PICK A JOB</div>
              <button className="jf-hover-blue" style={styles.pickerClose} onClick={() => { setShowJobsPicker(false); setJobSearch(''); }}>CLOSE</button>
            </div>

            <input
              type="text"
              placeholder="Search jobs..."
              value={jobSearch}
              onChange={(e) => setJobSearch(e.target.value)}
              style={{ margin: '10px', padding: '8px', border: '3px solid black', fontWeight: 700, fontSize: '14px', width: 'calc(100% - 20px)', boxSizing: 'border-box' }}
              autoFocus
            />

            <div style={styles.pickerList}>
              {(cannedJobs || []).filter(j => !jobSearch || (j.jobName || '').toLowerCase().includes(jobSearch.toLowerCase())).map(j => (
                <div
                  key={j.id}
                  style={styles.pickerItem}
                  onClick={() => {
                    addItemFromJob(j);
                    setShowJobsPicker(false);
                    setJobSearch('');
                  }}
                >
                  {j.jobName} — {money(Number(j.price || 0))}
                </div>
              ))}

              {(cannedJobs || []).filter(j => !jobSearch || (j.jobName || '').toLowerCase().includes(jobSearch.toLowerCase())).length === 0 && (
                <div style={{ fontWeight: 900, padding: '12px' }}>{(cannedJobs || []).length === 0 ? 'No canned jobs yet.' : 'No jobs match your search.'}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* QUICK ADD CUSTOMER MODAL */}
      {showQuickAddCustomer && !isLockedFromEstimate && (
        <QuickAddCustomerModal
          onClose={() => setShowQuickAddCustomer(false)}
          onSave={saveQuickCustomer}
        />
      )}
    </div>
  );
};

export default InvoiceEntry;
